import {useEffect, useMemo, useState} from 'react';

import {liveAccessService} from '@/services/liveAccessService';
import {merchantService} from '@/services/merchants';
import {userService} from '@/services/userService';
import type {BusinessLiveAccessNote, BusinessLiveAccessView} from '@/types';
import {BusinessLiveAccessActorType} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

/**
 * Loads a single live-access request and resolves the human-readable names needed by the
 * logs view:
 * - admin actors -> admin profile name (userService.getUser)
 * - business actors / the "For" field -> the business name (merchantService.getBusinessDetails)
 * `isLoading` stays true until the request AND all names are resolved, so the UI never flashes
 * raw ids or "ADMIN"/"BUSINESS" placeholders.
 *
 * The request is only returned when it matches both ids in the current URL. A link naming the
 * wrong business (or a URL that has moved on to another request) reads as not found rather than
 * showing a request under an address that does not belong to it. The check runs on every render,
 * so it follows the URL without any reload. It is a UI check only: the API looks the request up
 * by its id alone.
 */
export const useBusinessLiveAccessLog = (
  requestId?: string,
  businessId?: string,
) => {
  const [loadedRequest, setLoadedRequest] =
    useState<BusinessLiveAccessView | null>(null);
  const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(true);
  const [isResolving, setIsResolving] = useState<boolean>(true);
  const [adminNames, setAdminNames] = useState<Record<string, string>>({});
  const [businessName, setBusinessName] = useState<string>('');

  // 1. Fetch the request.
  useEffect(() => {
    let active = true;
    const fetchRequest = async () => {
      if (!requestId) {
        setIsLoadingRequest(false);
        return;
      }
      try {
        setIsLoadingRequest(true);
        const data = await liveAccessService.getById(requestId);
        if (active) {
          setLoadedRequest(data);
        }
      } catch (e) {
        showErrorMessage(getErrorMessage(e, 'Error loading live access logs'));
      } finally {
        if (active) {
          setIsLoadingRequest(false);
        }
      }
    };
    // `fetchRequest` toasts its own failures, so this only fires if that catch
    // or the finally itself throws. Log rather than swallow silently.
    fetchRequest().catch(logRejection('Failed to load live access request'));
    return () => {
      active = false;
    };
  }, [requestId]);

  const request =
    loadedRequest &&
    loadedRequest.id === requestId &&
    loadedRequest.businessId === businessId
      ? loadedRequest
      : null;

  const notes = useMemo(() => request?.notes ?? [], [request]);
  const adminIds = useMemo(
    () =>
      Array.from(
        new Set(
          notes
            .filter((n) => n.actorType === BusinessLiveAccessActorType.Admin)
            .map((n) => n.changedBy)
            .filter(Boolean),
        ),
      ),
    [notes],
  );

  // 2. Resolve admin + business names once the request is loaded. Live access is always
  // about the business itself, so the business name is always needed (for business actors
  // and the "For" field).
  useEffect(() => {
    let active = true;
    const resolveNames = async () => {
      if (!request) {
        return;
      }
      setIsResolving(true);
      try {
        const [adminEntries, resolvedBusinessName] = await Promise.all([
          Promise.all(
            adminIds.map(async (id): Promise<[string, string]> => {
              try {
                const user = await userService.getUser(id);
                return [id, user?.name?.fullName || user?.currentEmail || id];
              } catch {
                return [id, id];
              }
            }),
          ),
          request.businessId
            ? merchantService
                .getBusinessDetails(request.businessId)
                .then((d) => d?.business?.name || '')
                .catch(() => '')
            : Promise.resolve(''),
        ]);
        if (active) {
          setAdminNames(Object.fromEntries(adminEntries));
          setBusinessName(resolvedBusinessName);
        }
      } finally {
        if (active) {
          setIsResolving(false);
        }
      }
    };
    // Every lookup inside `resolveNames` falls back to the raw id, and the
    // function has a finally but no catch, so an unexpected throw would escape
    // unhandled. Names stay unresolved either way; just log it.
    resolveNames().catch(
      logRejection('Failed to resolve live access actor names'),
    );
    return () => {
      active = false;
    };
  }, [request, adminIds]);

  const resolveActor = (note: BusinessLiveAccessNote): string =>
    note.actorType === BusinessLiveAccessActorType.Admin
      ? adminNames[note.changedBy] || 'Admin'
      : businessName || 'Business';

  const forLabel = businessName || '—';

  const isLoading = isLoadingRequest || (!!request && isResolving);

  return {request, isLoading, resolveActor, forLabel};
};

export default useBusinessLiveAccessLog;
